import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import { Alert, Box, Button, LinearProgress, Paper, Stack, TextField } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { useTranslation } from 'react-i18next';
import {
  SMART_COLORS as COLORS,
  smartCardSx as cardSx,
} from '../../theme/smartStableTheme';
import SettingsPageHeader from '../../components/SettingsPageHeader';

const apiUrl = process.env.REACT_APP_API_URL;

const FarrierMaterialsEdit = () => {
  const { medicationId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRtl = String(i18n.language || '').startsWith('ar');
  const [material, setMaterial] = useState({
    medication_name: '',
    stock_quantity: '',
    price: '',
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api.get(`${apiUrl}/farrier_materials/${medicationId}`)
      .then((response) => {
        if (mounted) setMaterial(response.data || {});
      })
      .catch((err) => {
        if (mounted) setError(err?.response?.data?.error || t('farrierMaterials.loadError'));
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [medicationId, t]);

  const updateField = (field, value) => setMaterial((current) => ({ ...current, [field]: value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.put(`${apiUrl}/farrier_materials/${medicationId}`, material);
      navigate('/farrier-materials');
    } catch (err) {
      setError(err?.response?.data?.error || t('farrierMaterials.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box className="smart-page-container" dir={isRtl ? 'rtl' : 'ltr'} sx={{ bgcolor: COLORS.page, minHeight: '100vh', maxWidth: 'var(--app-page-max)', mx: 'auto' }}>
      <SettingsPageHeader
        title={t('farrierMaterials.editTitle')}
        subtitle={t('farrierMaterials.formHint')}
        backTo="/farrier-materials"
      />

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, md: 3 } }}>
        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField label={t('farrierMaterials.name')} value={material.medication_name || ''} onChange={(event) => updateField('medication_name', event.target.value)} required fullWidth />
            <TextField label={t('farrierMaterials.stock')} type="number" value={material.stock_quantity ?? ''} onChange={(event) => updateField('stock_quantity', event.target.value)} inputProps={{ min: 0, step: '1' }} fullWidth />
            <TextField label={t('farrierMaterials.price')} type="number" value={material.price ?? ''} onChange={(event) => updateField('price', event.target.value)} inputProps={{ min: 0, step: '0.01' }} fullWidth />
            <Stack direction="row" spacing={1.5} justifyContent="flex-end">
              <Button onClick={() => navigate('/farrier-materials')} sx={{ color: COLORS.muted, fontWeight: 900 }}>{t('cancel')}</Button>
              <Button type="submit" disabled={saving} variant="contained" startIcon={<SaveIcon />} sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover }, fontWeight: 900 }}>{t('save')}</Button>
            </Stack>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
};

export default FarrierMaterialsEdit;
